import { useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { submitMeasurement } from '../../services/measurementService';
import './NewTankSection.css';

const FIELDS = [
  { id: 'ammonia', max: 10 },
  { id: 'nitrite', max: 10 },
  { id: 'nitrate', max: 200 },
] as const;
type FieldId = (typeof FIELDS)[number]['id'];

export interface CycleTestDialogProps {
  aquariumId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/** Guided cycle test (#1825): NH3 and NO2 (required) and NO3 (optional) in ppm, saved as one test time. */
export function CycleTestDialog({ aquariumId, isOpen, onClose, onSaved }: CycleTestDialogProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<FieldId, string>>({ ammonia: '', nitrite: '', nitrate: '' });
  const [errors, setErrors] = useState<Partial<Record<FieldId, string>>>({});
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<FieldId, string>> = {};
    for (const f of FIELDS) {
      const raw = values[f.id].trim().replace(',', '.');
      if (raw === '') {
        if (f.id !== 'nitrate') next[f.id] = t('newTank.test.required');
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0 || n > f.max) next[f.id] = t('newTank.test.range', { max: f.max });
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    setSaveError(false);
    const measuredAt = new Date().toISOString();
    try {
      for (const f of FIELDS) {
        const raw = values[f.id].trim().replace(',', '.');
        if (raw === '') continue;
        await submitMeasurement({ aquariumId, parameterId: f.id, parameterName: f.id, value: Number(raw), unit: 'ppm', measuredAt });
      }
      setValues({ ammonia: '', nitrite: '', nitrate: '' });
      onSaved();
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={t('newTank.test.title')} size="sm" data-testid="cycle-test-dialog">
      <form onSubmit={submit} noValidate>
        <Stack gap="4">
          <Text>{t('newTank.test.intro')}</Text>
          {saveError && <Alert>{t('newTank.test.saveError')}</Alert>}
          {FIELDS.map((f) => (
            <Input
              key={f.id}
              id={`cycle-test-${f.id}`}
              type="text"
              inputMode="decimal"
              label={t(`newTank.test.${f.id}`)}
              value={values[f.id]}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f.id]: e.target.value }));
                setErrors((er) => ({ ...er, [f.id]: undefined }));
              }}
              error={errors[f.id]}
              required={f.id !== 'nitrate'}
              disabled={saving}
            />
          ))}
          <div className="new-tank__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              {t('newTank.cancel')}
            </Button>
            <Button type="submit" isLoading={saving}>
              {t('newTank.test.submit')}
            </Button>
          </div>
        </Stack>
      </form>
    </Dialog>
  );
}
